import "../../styles/BasicIA.css";
import * as React from 'react';
import * as styles from './ContentOrganizerWizard.css';
import { IAWizard } from './wizard/Wizard';
import { IAIcon } from '../icon/Icon';
import * as constants from '../../global/Constants';
import { ContentOrganizerStore } from './stores';
import { ProgressBar } from "./progressBar/ProgressBar";
import { IAButton } from "../button/Button";
import { Provider } from 'mobx-react';
import { ContentOrganizerCreatePanel } from "./panels/ContentOrganizerCreatePanel";
import { ContentOrganizerOptionsPanel } from "./panels/ContentOrganizerOptionsPanel";
import { ContentOrganizerConfigPanel } from "./panels/ContentOrganizerConfigPanel";
import { ContentOrganizerPermissionsTargetingPanel } from "./panels/ContentOrganizerPermissionsTargetingPanel";
import { IContentOrganizerCreate } from "./../../interfaces/IContentOrganizerCreate";
import ContentOrganizerService from "./../../services/ContentOrganizerService";
import { IDurableFunctionRuntimeStatus } from "../../interfaces/IDurableFunctionRuntimeStatus";
import { LocalizationService } from "../../services/LocalizationService";
import { IAPopup } from "../popup/Popup";
import { Helper } from "../../Helper";
import { IApplication, Instance } from "../../interfaces/IApplication";
import { TargetingService } from "../../services/TargetingService";
import { ConditionalOutputFactory } from "../../services/ConditionalOutputFactory";
import { IAMessageBar } from '../messageBar/MessageBar';
import AdvancedSettingsService from "../../services/AdvancedSettingsService";

export interface IEditProps {
  isEditMode: boolean;
  instanceName?: string;
}

export interface IProps {
  token: string;
  tenant: string;
  environment: "Development" | "Test" | "Production",
  show: boolean;
  isMobile: boolean;
  editProps: IEditProps;
  onClose: () => void;
  showDeletePopup: () => void;
}

export interface IState {
  show: boolean;
  selectedStep: number;
  isCreating: boolean;
  isLoading: boolean;
  permissionIssues: string[]
  storeInitializationComplete: boolean;
}

export class IAContentOrganizerWizard extends React.Component<IProps, IState> {

  private contentOrganizerStore: ContentOrganizerStore;
  private focusRef: React.RefObject<HTMLDivElement>;
  private cancelPopupId;

  constructor(props: IProps) {
    super(props);
    this.state = {
      show: props.show,
      selectedStep: 0,
      isCreating: false,
      isLoading: false,
      permissionIssues: [],
      storeInitializationComplete: false,
    };

    this.focusRef = React.createRef();

    // Content Organizer Store
    this.contentOrganizerStore = new ContentOrganizerStore();
    this.initializeStoreToDefaultCreateView(props, window.navigator.language);
    this.cancelPopupId = Helper.getRandomStringKey();
  }

  public componentWillUnmount(): void {
    document.removeEventListener("keydown", (event) => this.keyListener(event), false);
  }

  public componentDidMount(): void {
    document.addEventListener("keydown", (event) => this.keyListener(event), false);
    this.checkPermissionsForCurrentUser();
    AdvancedSettingsService.getSetting(this.props.environment, this.props.token, this.props.tenant, "enableADTargetingComponent").then(enableADTargetingComponent => {
      this.contentOrganizerStore.hideProfileInformation = enableADTargetingComponent?.value === "true";
    });
  }

  private keyListener(event: KeyboardEvent): void {
    if (event.keyCode === constants.ESC_KEY_CODE) {
      this.escFunction();
    }
  }

  private escFunction(): void {
    this.contentOrganizerStore.showCancelEditPopup = true;
    this.setState({ show: this.state.show });
  }

  public componentWillReceiveProps(props: IProps): void {
    if (props.show && this.focusRef.current) {
      this.focusRef.current.focus();
    }

    this.setState({ show: props.show });

    if (props.editProps.isEditMode !== this.props.editProps.isEditMode) {
      this.initializeStoreToDefaultCreateView(props, window.navigator.language);
    }
  }

  private async checkStatusIsInstanceCreated(): Promise<boolean> {
    return await new Promise(resolve => {
      const interval = setInterval(async () => {
        let contentOrganizerStatus = await ContentOrganizerService.getContentOrganizerStatus(
          this.contentOrganizerStore.environment,
          this.contentOrganizerStore.token,
          this.contentOrganizerStore.tenant,
          this.contentOrganizerStore.siteCollectionAddress);
        if (contentOrganizerStatus.runtimeStatus === IDurableFunctionRuntimeStatus.Completed ||
          (contentOrganizerStatus.customStatus && contentOrganizerStatus.customStatus.currentStep > 1)) {
          clearInterval(interval);
          resolve(true);
        } else if (contentOrganizerStatus.runtimeStatus === IDurableFunctionRuntimeStatus.Failed) {
          clearInterval(interval);
          resolve(false);
        }
      }, 1000);
    });
  }

  private async checkPermissionsForCurrentUser(): Promise<void> {
    const response = await ContentOrganizerService.checkPermissionsForCurrentUser(
      this.contentOrganizerStore.environment,
      this.contentOrganizerStore.token,
      this.contentOrganizerStore.tenant);

    if (response == null || response.issues.length == 0) {
      return;
    }

    let permissionIssues = [];
    const issuesExpression = new RegExp(response.issues.join('|'));

    if (issuesExpression.test('CannotCreateInstance|CannotManagePermissions')) {
      permissionIssues.push(new LocalizationService().strings.ContentOrganizer_PermissionIssueNotContentOrganizerSiteOwner);
    }

    this.setState({ permissionIssues })
  }

  private saveTargeting() {
    let isEnable = this.contentOrganizerStore.enableTargeting;
    let allValues = this.contentOrganizerStore.allValues;
    let type = this.contentOrganizerStore.radioValue;
    if (this.contentOrganizerStore.selectedValues.length === 0) {
      this.contentOrganizerStore.selectedValues = this.contentOrganizerStore.storedValues
    }

    let application: IApplication = undefined;
    if (this.contentOrganizerStore.enableTargeting) {
      application = this.contentOrganizerStore.applicationsData.find(x => x.name.toLowerCase() === this.contentOrganizerStore.applicationName.toLowerCase());
      let data: Instance = {
        id: this.contentOrganizerStore.contentOrganizerTitle,
        name: this.contentOrganizerStore.contentOrganizerTitle,
        isEnable: isEnable,
        useDefault: false,
        allValues: allValues,
        values: type === 'userGroups' ? this.contentOrganizerStore.selectedValues : this.contentOrganizerStore.treeValues,
        type: type
      };

      let indexToDelete;
      application?.instances?.forEach((instance: Instance, index) => {
        if (instance?.id === data?.id) {
          indexToDelete = index;
        }
      });
      if (indexToDelete) {
        application?.instances?.splice(indexToDelete, 1);
      }
      application.instances.push(data);
    }

    let listObj = {
      id: application.id,
      title: application.title,
      applicationSettings: type === 'userGroups' ? 'User groups' : 'Profile data',
      defaultValues: JSON.stringify(application)
    }

    TargetingService.updateApplicationsTargetSettings(this.props.environment, this.props.tenant, this.props.token,
      listObj, application.id).then(() => {
        this.contentOrganizerStore.selectedValues = [];
        this.contentOrganizerStore.storedValues = [];
        this.contentOrganizerStore.isSaving = false;
      });
  }

  public render(): JSX.Element {
    const content: JSX.Element = (
      <div>
        <div className={styles.IA_contentOrganizer}
          ref={this.focusRef}>
          {this.props.onClose != undefined &&
            <div>
              <IAIcon
                title="Close"
                size={18}
                color="#777777"
                onClick={() => {
                  this.contentOrganizerStore.showCancelEditPopup = true;
                  this.setState({ show: this.state.show });
                }}
                style={{
                  cursor: "pointer",
                  marginLeft: 10,
                  position: "absolute",
                  right: 20,
                  top: 20
                }}
              />
            </div>
          }
        </div>
        <Provider contentOrganizerStore={this.contentOrganizerStore}>
          <div className={styles.IA_contentOrganizer_innerpanels}>
            {this.state.permissionIssues.length > 0 &&
              <div className={styles.IA_contentOrganizer_issues}>
                {this.state.permissionIssues.map((issueCode) => (
                  <IAMessageBar
                    type="warning"
                    onDismiss={() => { }}
                    icon="Attention"
                    content={issueCode}
                    showIcon={true}
                    showCloseButton={false}
                  />
                ))}
              </div>
            }
            {(this.contentOrganizerStore.selectedStep == 0 && !this.contentOrganizerStore.isEditMode) &&
              <ContentOrganizerOptionsPanel
                onSelectedStep={(step) => {
                  this.contentOrganizerStore.setStep(step);
                  this.setState({
                    selectedStep: step
                  });
                }}
              />}
            {this.contentOrganizerStore.selectedStep == 1 &&
              <ContentOrganizerCreatePanel
                isNewSiteCollection={this.contentOrganizerStore.newSiteCollection}
                onSelectedStep={(step) => {
                  this.contentOrganizerStore.setStep(step);
                  this.setState({
                    selectedStep: step
                  });
                }}
              />}
            {(this.contentOrganizerStore.selectedStep == 2 || (this.contentOrganizerStore.selectedStep == 0 && this.contentOrganizerStore.isEditMode)) &&
              <ContentOrganizerConfigPanel
                beforeIaReady={() => {
                  this.setState({ isLoading: true });
                }}
                afterIaReady={() => {
                  this.setState({ isLoading: false });
                }}
                onSelectedStep={(step) => {
                  this.contentOrganizerStore.setStep(step);
                  this.setState({
                    selectedStep: step
                  });
                }}
                storeInitializationComplete={
                  this.state.storeInitializationComplete
                }
              />
            }
            {this.contentOrganizerStore.selectedStep == 3 &&
              <ContentOrganizerPermissionsTargetingPanel
                beforeIaReady={() => {
                  this.setState({ isLoading: true });
                }}
                afterIaReady={() => {
                  this.setState({ isLoading: false });
                }}
                onSelectedStep={(step) => {
                  this.contentOrganizerStore.setStep(step);
                  this.setState({
                    selectedStep: step
                  });
                }}
              />}
          </div>
        </Provider>

        <div style={{ height: 35 }}>
          {this.contentOrganizerStore.isEditMode &&
            <IAButton
              label={new LocalizationService().strings.Admin_Delete}
              style={{
                marginRight: "10px",
                float: "right",
                right: 230,
                bottom: 20
              }}
              borderRadius={5}
              buttonColor="red"
              arialable="Show"
              onClick={() => this.props.showDeletePopup()}
            />
          }
          <IAButton
            label={new LocalizationService().strings.ContentOrganizer_FooterCancelButton}
            style={{
              marginRight: "10px",
              float: "right",
              right: 230,
              bottom: 20
            }}
            borderRadius={5}
            buttonColor="#eee"
            arialable="Show"
            onClick={() => {
              this.contentOrganizerStore.showCancelEditPopup = true;
              this.setState({ show: this.state.show });
            }}
          />
          <IAButton
            label={ConditionalOutputFactory
              .WithDefault(new LocalizationService().strings.ContentOrganizer_FooterNextButton)
              .OnCondition(this.contentOrganizerStore.selectedStep == this.contentOrganizerStore.lastStepIndex, new LocalizationService().strings.ContentOrganizer_FooterCreateButton)
              .OnCondition(this.contentOrganizerStore.isEditMode, new LocalizationService().strings.ContentOrganizer_FooterUpdateButton)
              .Last()}
            style={{
              marginRight: "10px",
              float: "right",
              right: 110,
              bottom: 20,
            }}
            borderRadius={5}
            disbaled={(this.state.isCreating || this.state.isLoading) ? true : this.contentOrganizerStore.selectedStep !== 0 ? this.contentOrganizerStore.checkData() : this.contentOrganizerStore.selectedStep == 0 ? !this.contentOrganizerStore.isEditMode : false}
            showSpinner={this.state.isCreating || this.state.isLoading}
            buttonColor="#1c899d"
            hasFixedSize={true}
            fixedSize="0 40px"
            arialable="Next"
            onClick={async () => {
              console.log("You just clicked step " + this.contentOrganizerStore.selectedStep.toString());
              if (this.contentOrganizerStore.selectedStep == this.contentOrganizerStore.lastStepIndex) {
                this.setState({ isCreating: true });
                console.log("Creating...");
                var permissions = [];
                if (this.contentOrganizerStore.usePermissions) {
                  this.contentOrganizerStore.permissions.forEach(item => {
                    permissions.push({
                      role: item.role,
                      loginName: item.user.userName
                    });
                  })
                }
                console.log("building object...");
                var contentOrganizer: IContentOrganizerCreate = {
                  title: this.contentOrganizerStore.contentOrganizerTitle,
                  description: this.contentOrganizerStore.contentOrganizerDescription,
                  siteCollection: this.contentOrganizerStore.siteCollectionAddress,
                  siteLanguage: this.contentOrganizerStore.selectedLanguage,
                  template: this.contentOrganizerStore.selectedTemplate,
                  siteOwnersWithGroups: this.contentOrganizerStore.siteOwners ?? [],
                  timeZoneId: Number.parseInt(this.contentOrganizerStore.selectedTimeZone),
                  metadata: {
                    isFeatureSelected: this.contentOrganizerStore.useMetadata,
                    required: this.contentOrganizerStore.isUseMetadataRequired,
                    termSetId: this.contentOrganizerStore.termSetId
                  },
                  responsible: {
                    isFeatureSelected: this.contentOrganizerStore.usePageResponsible,
                    required: this.contentOrganizerStore.isUsePageReesponsibleRequired
                  },
                  revision: {
                    daysBeforeRevisionIsRequired: this.contentOrganizerStore.revisioDateDays,
                    isFeatureSelected: this.contentOrganizerStore.useRevisionDate,
                    required: this.contentOrganizerStore.isUseRevisionDateRequired
                  },
                  elements: {
                    sharepointPage: false
                  },
                  enablePushNotifications: false,
                  permissions: {
                    initialPermissions: permissions,
                    isFeatureSelected: this.contentOrganizerStore.usePermissions
                  },
                  navigator: {
                    isFeatureSelected: this.contentOrganizerStore.useNavigator,
                    instance: {
                      id: this.contentOrganizerStore.selectedNavigatorInstance?.id,
                      instanceName: this.contentOrganizerStore.selectedNavigatorInstance?.instanceName,
                      title: this.contentOrganizerStore.selectedNavigatorInstance?.title,
                      hideHubMenu: this.contentOrganizerStore.hideHubMenu,
                      hideLocalMenu: this.contentOrganizerStore.hideLocalMenu,
                    }
                  },
                  hubSite: {
                    isFeatureSelected: this.contentOrganizerStore.useHubSite,
                    title: this.contentOrganizerStore.selectedHubSite?.title,
                    id: this.contentOrganizerStore.selectedHubSite?.id
                  },
                  documentLibraries: this.contentOrganizerStore?.documentLibraries,
                  featuredImageUrl: this.contentOrganizerStore?.selectedFeaturedImageUrl
                };
                console.log("Calling API...");
                await ContentOrganizerService.createContentOrganizer(this.contentOrganizerStore.environment, this.contentOrganizerStore.token, this.contentOrganizerStore.tenant, contentOrganizer);
                if (this.contentOrganizerStore.enableTargeting) {
                  this.saveTargeting();
                }
                // Wait until instance is created
                const checkStatusIsInstanceCreated = await this.checkStatusIsInstanceCreated();
                if (checkStatusIsInstanceCreated) {
                  this.setState({ show: false }, () => {
                    this.contentOrganizerStore.clearData();
                    this.props.onClose();
                  });
                  // Redirect to Admin page
                  window.location.href = `https://${this.contentOrganizerStore.tenant}.sharepoint.com/sites/IntraActive-Admin?component=IntraActive-ContentOrganizer&instance=${contentOrganizer.siteCollection}`;
                } else {
                  // Let user know an error has happened
                }
              } else {
                this.contentOrganizerStore.setStep(this.contentOrganizerStore.selectedStep + 1);
                this.setState({
                  selectedStep: this.contentOrganizerStore.selectedStep
                });
              }
            }
            }
          />
          {!this.contentOrganizerStore.isEditMode &&
            < IAButton
              label={new LocalizationService().strings.ContentOrganizer_FooterBackButton}
              style={{
                marginRight: "10px",
                float: "right",
                right: 20,
                bottom: 20,
              }}
              borderRadius={5}
              styleLabel={{ padding: "0 6px" }}
              disbaled={this.contentOrganizerStore.selectedStep === 0}
              buttonColor="#eee"
              arialable="Show"
              onClick={() => {
                if (this.contentOrganizerStore.selectedStep !== 0) {
                  this.contentOrganizerStore.setStep(this.contentOrganizerStore.selectedStep - 1);
                  this.setState({
                    selectedStep: this.contentOrganizerStore.selectedStep
                  });
                }
              }
              }
            />
          }

        </div>

        {/* Cancel pop-up */}
        <div id={`cancelPopupId_${this.cancelPopupId}`}>
          {this.contentOrganizerStore.showCancelEditPopup &&
            <IAPopup
              headline={new LocalizationService().strings.Messages_CancelPopup_Headline}
              content={
                <div>
                  <IAButton
                    label={new LocalizationService().strings.Messages_CancelPopup_Yes}
                    buttonColor={this.contentOrganizerStore.highlightColor}
                    showSpinner={false}
                    onClick={() => {
                      this.contentOrganizerStore.clearData();
                      this.contentOrganizerStore.showCancelEditPopup = false;
                      this.setState({ show: false }, this.props.onClose);
                      // If the panel closes like this, it does not reset the body styles
                      // document.body.style.overflowY = "auto";
                      // document.body.style.height = "inherit";
                    }}
                    style={{
                      marginTop: 20,
                      float: "left"
                    }}
                  />
                  <IAButton
                    label={new LocalizationService().strings.Messages_CancelPopup_No}
                    buttonColor={"#999999"}
                    showSpinner={false}
                    onClick={() => {
                      this.contentOrganizerStore.showCancelEditPopup = false;
                      this.setState({ show: this.state.show });
                    }}
                    style={{
                      marginTop: 20,
                      marginLeft: 10,
                      float: "left"
                    }}
                  />
                </div>
              }
              close={() => {
                this.contentOrganizerStore.showCancelEditPopup = false;
                this.setState({ show: this.state.show });
              }}
            />
          }
        </div>
      </div>


    );

    return (
      <div>
        <Provider contentOrganizerStore={this.contentOrganizerStore}>
          <IAWizard
            open={this.state.show}
            isMobile={this.props.isMobile}
            transition="slide"
            width={this.props.isMobile ? window.innerWidth : window.innerWidth - 250}
            close={() => {
              this.contentOrganizerStore.clearData();
              this.contentOrganizerStore.showCancelEditPopup = false;
              this.setState({ show: false }, this.props.onClose);
            }}
            onCancel={() => {
              this.contentOrganizerStore.showCancelEditPopup = true;
              this.setState({ show: this.state.show });
            }}
            disableESCFunction={true}
          >
            <ProgressBar
              selectedStep={this.contentOrganizerStore.selectedStep}
              isEditMode={this.contentOrganizerStore.isEditMode}
              onSelectedStep={(step) => {
                this.contentOrganizerStore.setStep(step);
                this.setState({
                  selectedStep: step
                });
              }}
            />
            {content}
          </IAWizard>
        </Provider>
      </div>
    );
  }

  private initializeStoreToDefaultCreateView(props: IProps, language: string) {
    this.contentOrganizerStore.component = "ContentOrganizerWizard";
    this.contentOrganizerStore.applicationName = "Content Organizer";
    this.contentOrganizerStore.isMobile = this.props.isMobile;
    this.contentOrganizerStore.lastStepIndex = 3;
    this.contentOrganizerStore.setStep(0);
    this.contentOrganizerStore.tenant = props.tenant;
    this.contentOrganizerStore.token = props.token;
    this.contentOrganizerStore.environment = props.environment;
    this.contentOrganizerStore.selectedTimeZone = "3";
    this.contentOrganizerStore.selectedTemplate = "handbook";
    this.contentOrganizerStore.permissions = [];
    this.contentOrganizerStore.isEditMode = false;
    const SupportedLanguages = [
      { key: "da-DK", value: new LocalizationService().strings.ContentOrganizer_Danish },
      { key: "de-DE", value: new LocalizationService().strings.ContentOrganizer_German },
      { key: "en-US", value: new LocalizationService().strings.ContentOrganizer_English },
      { key: "pt-BR", value: new LocalizationService().strings.ContentOrganizer_Portuguese },
      { key: "sv-SE", value: new LocalizationService().strings.ContentOrganizer_Swedish }
    ];
    if (SupportedLanguages.some(x => x.key === language)) {
      this.contentOrganizerStore.selectedLanguage = language;
    } else {
      this.contentOrganizerStore.selectedLanguage = "en-US";
    }

    if (props.editProps.isEditMode) {
      this.setState({ isLoading: true });
      this.contentOrganizerStore.isEditMode = true;
      this.contentOrganizerStore.lastStepIndex = 0;
      ContentOrganizerService.getContentOrganizerInstanceSettings(this.contentOrganizerStore.environment, this.contentOrganizerStore.token, this.contentOrganizerStore.tenant, props.editProps.instanceName)
        .then(instance => {
          this.contentOrganizerStore.newSiteCollection = false;
          this.contentOrganizerStore.contentOrganizerTitle = instance.settings.title;
          this.contentOrganizerStore.contentOrganizerDescription = instance.settings.description;
          this.contentOrganizerStore.siteCollectionAddress = instance.settings.siteCollection;
          this.contentOrganizerStore.selectedLanguage = instance.settings.siteLanguage;
          this.contentOrganizerStore.selectedTemplate = instance.settings.template;
          this.contentOrganizerStore.siteOwners = instance.settings.siteOwnersWithGroups ?? [];
          this.contentOrganizerStore.selectedTimeZone = instance.settings.timeZoneId.toString();

          this.contentOrganizerStore.useMetadata = instance.settings.metadata.isFeatureSelected;
          this.contentOrganizerStore.isUseMetadataRequired = instance.settings.metadata.required;
          this.contentOrganizerStore.termSetId = instance.settings.metadata.termSetId;
          this.contentOrganizerStore.usePageResponsible = instance.settings.responsible.isFeatureSelected;
          this.contentOrganizerStore.isUsePageReesponsibleRequired = instance.settings.responsible.required;
          this.contentOrganizerStore.revisioDateDays = instance.settings.revision.daysBeforeRevisionIsRequired;
          this.contentOrganizerStore.useRevisionDate = instance.settings.revision.isFeatureSelected;
          this.contentOrganizerStore.isUseRevisionDateRequired = instance.settings.revision.required;
          this.contentOrganizerStore.selectedHubSite = { id: instance.settings.hubSite.id, title: instance.settings.hubSite.title };
          this.contentOrganizerStore.selectedNavigatorInstance = { id: instance.settings.navigator.instance?.id, instanceName: instance.settings.navigator.instance.instanceName, title: instance.settings.navigator.instance.title };
          this.contentOrganizerStore.useNavigator = instance.settings.navigator.isFeatureSelected;
          this.contentOrganizerStore.useHubSite = instance.settings.hubSite.isFeatureSelected;
          this.contentOrganizerStore.documentLibraries = instance.settings.documentLibraries;

          this.contentOrganizerStore.selectedFeaturedImageUrl = instance.settings.featuredImageUrl;

          this.contentOrganizerStore.setStep(0);
          this.setState({ isLoading: false });
          this.setState({ storeInitializationComplete: true });
        });
    } else {
      this.setState({ storeInitializationComplete: true });
    }
  }
}